import { useCallback, useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { type LabelStack } from '@atlaskit/react-ufo/interaction-context';
import { addSpanToAll } from '@atlaskit/react-ufo/interaction-metrics';
import { ExperienceTracker as ExperienceTrackerBase } from '@atlassian/experience-tracker';
import type {
	ExperienceTrackerAPI as ExperienceTrackerAPIBase,
	FailOptions,
} from '@atlassian/experience-tracker/ExperienceTracker';

import { isClientFetchError } from '../../common/utils/is-error';
import { type RovoInstrumentedError } from '../../common/utils/rovo-instrumented-error';
import { logException } from '../../services/sentry';
import { useAnalytics } from '../analytics';

import { ExperienceNamePostfix } from './constants';

// Re-exporting some experience trackers packages
// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable-next-line no-barrel-files/no-barrel-files */
export { ExperienceTrackerContext, ExperienceTracker } from '@atlassian/experience-tracker';

type FailOptionsWithTeamName = FailOptions & {
	/**
	 * The sentry team name
	 */
	teamName?: string;
};

/**
 * A slighly augmented version of the ExperienceTrackerAPI interface to add the teamName attribute to the fail method
 * The attributes are added by the proxy handler below
 */
export interface ExperienceTrackerAPI extends ExperienceTrackerAPIBase {
	fail(options: FailOptionsWithTeamName): void;
}

const isRovoInstrumentedError = (error: any): error is RovoInstrumentedError =>
	error &&
	typeof error === 'object' &&
	'errorAttributes' in error &&
	typeof error.errorAttributes === 'object';

class ExperienceTrackerHandler {
	private static instance: ExperienceTrackerHandler;
	private static labelStack: LabelStack = [];
	public experienceTracker: ExperienceTrackerAPI = new ExperienceTrackerBase();
	private sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent'] | null = null;

	constructor() {
		this.experienceTracker.subscribe((event) => {
			let filteredErrorAttributes = {};
			const isExtendedErrorTracking = fg('rovo_extended_error_tracking');
			let preventSendingLogException = false;

			// for FailEvent
			if ('error' in event) {
				// do not fail experience tracking if the error is a client fetch error
				if (isClientFetchError(event.error)) {
					preventSendingLogException = true;
				}

				if (event.attributes) {
					const { webSearchEnabled, deepResearchEnabled, thinkDeeperEnabled } =
						event.attributes as Record<string, any>;

					if (webSearchEnabled !== undefined) {
						filteredErrorAttributes = {
							...filteredErrorAttributes,
							webSearchEnabled,
						};
					}

					if (deepResearchEnabled !== undefined) {
						filteredErrorAttributes = {
							...filteredErrorAttributes,
							deepResearchEnabled,
						};
					}

					if (thinkDeeperEnabled !== undefined) {
						filteredErrorAttributes = {
							...filteredErrorAttributes,
							thinkDeeperEnabled,
						};
					}
				}

				if (isRovoInstrumentedError(event.error)) {
					const errorAttributes = event.error.errorAttributes;

					// Remove undefined properties
					filteredErrorAttributes = Object.fromEntries(
						Object.entries(errorAttributes).filter(([_, value]) => value !== undefined),
					);

					// Filter 5xx errors since backend monitoring will cover this
					// as per https://hello.atlassian.net/wiki/spaces/CONVAI/pages/3547354267/Meeting+notes+FE+SLOs+which+one+to+promote
					if (errorAttributes?.statusCode && errorAttributes?.statusCode >= 500) {
						preventSendingLogException = true;
					}
				}

				if (!preventSendingLogException) {
					const error =
						event.error instanceof Error ? event.error : new Error(JSON.stringify(event.error));

					const attributes = event.attributes as Record<string, any> | undefined;
					// this is added by `useExperienceTracker` below
					const touchpointSource = attributes?.touchpointSource;
					const teamName = attributes?.teamName;

					logException({
						exception: error,
						name: event.name,
						tags: {
							source: 'aiMateExperienceTracker',
							...filteredErrorAttributes,
							...(touchpointSource && {
								touchpointSource,
							}),
							...(teamName && {
								teamName,
							}),
						},
					});
				}
			}

			// Track all failed events via analytics when isExtendedErrorTracking
			if (!preventSendingLogException || isExtendedErrorTracking) {
				if (
					event.action === 'taskSuccess' ||
					event.action === 'taskFail' ||
					event.action === 'taskAbort'
				) {
					// the experience tracker already uses performance.now() to calculate the duration, so we can just piggyback on that,
					// we will loose some precision due to rounding of duration, but it's good enough for our purposes
					addSpanToAll(
						'custom',
						`exp-${event.name}`,
						ExperienceTrackerHandler.labelStack,
						event.startTime,
						event.startTime + event.duration,
					);
				}

				this.sendAnalyticsEvent?.(
					{
						eventType: 'operational',
						action: event.action,
						actionSubject: 'ui',
						attributes: {
							task: event.name,
							taskId: event.id,
							startTime: event.startTime,
							// Only add duration if event is SuccessEvent or FailEvent
							...((event.action === 'taskSuccess' || event.action === 'taskFail') && {
								duration: event.duration,
							}),
							...(event.action === 'taskFail' && {
								error: event.error.message,
								...filteredErrorAttributes,
							}),
							...event.attributes,
						},
					},
					{
						removeDefaultTags: true,
					},
				);
			}
		});
	}

	setSendAnalyticsEvent(sendAnalyticsEvent: ReturnType<typeof useAnalytics>['sendAnalyticsEvent']) {
		this.sendAnalyticsEvent = sendAnalyticsEvent;
	}

	static getInstance() {
		if (!ExperienceTrackerHandler.instance) {
			ExperienceTrackerHandler.instance = new ExperienceTrackerHandler();
		}

		return ExperienceTrackerHandler.instance;
	}
	static setLabelStack(labelStack: LabelStack) {
		ExperienceTrackerHandler.labelStack = labelStack;
	}
}

type Params = {
	touchpointSource?: string;
};

export const useUFOLabelStackSetter = () => {
	return useCallback((componentLabelStack: LabelStack) => {
		ExperienceTrackerHandler.setLabelStack(componentLabelStack);
	}, []);
};

export const useAIMateExperienceTracker = (params?: Params): ExperienceTrackerAPI => {
	di(useAnalytics);
	const { sendAnalyticsEvent, mode: conversationAssistantUIMode } = useAnalytics();

	const getNameOverride = useCallback(
		(name: string) => {
			switch (conversationAssistantUIMode) {
				case 'mini-modal':
					return `${name}.${ExperienceNamePostfix.MINI_MODAL}`;
				default:
					return name;
			}
		},
		[conversationAssistantUIMode],
	);

	useMemo(() => {
		const experienceTracker = ExperienceTrackerHandler.getInstance();
		// this is not ideal, okay just because `sendAnalyticsEvent` is never change
		// since every hook instance will override the singleton sendAnalyticsEvent
		experienceTracker.setSendAnalyticsEvent(sendAnalyticsEvent);
	}, [sendAnalyticsEvent]);

	const experienceTrackerProxy = useMemo(() => {
		const proxyHandler = {
			get: (target: ExperienceTrackerAPI, prop: keyof ExperienceTrackerAPI) => {
				// Overriding the experienceTracker target methods to add additional attributes
				if (prop === 'fail') {
					return (failParams: Parameters<typeof target.fail>[0]) => {
						target.fail({
							...failParams,
							attributes: {
								...failParams.attributes,
								name: !!failParams.name ? getNameOverride(failParams.name) : failParams.name,
								touchpointSource: params?.touchpointSource,
								teamName: failParams.teamName,
							},
						});
					};
				}

				if (prop === 'succeed') {
					return (succeedParams: Parameters<typeof target.succeed>[0]) => {
						target.succeed({
							...succeedParams,
							name: !!succeedParams.name ? getNameOverride(succeedParams.name) : succeedParams.name,
							attributes: {
								...succeedParams.attributes,
								touchpointSource: params?.touchpointSource,
							},
						});
					};
				}

				if (prop === 'start') {
					return (startParams: Parameters<typeof target.start>[0]) => {
						target.start({
							...startParams,
							name: !!startParams.name ? getNameOverride(startParams.name) : startParams.name,
						});
					};
				}

				if (prop === 'abort') {
					return (abortParams: Parameters<typeof target.abort>[0]) => {
						target.abort({
							...abortParams,
							name: !!abortParams.name ? getNameOverride(abortParams.name) : abortParams.name,
						});
					};
				}

				if (prop === 'stopOnError') {
					return (stopOnErrorParams: Parameters<typeof target.stopOnError>[0]) => {
						target.stopOnError({
							...stopOnErrorParams,
							name: !!stopOnErrorParams.name
								? getNameOverride(stopOnErrorParams.name)
								: stopOnErrorParams.name,
						});
					};
				}

				return typeof target[prop] === 'function' ? target[prop].bind(target) : target[prop];
			},
		};

		return new Proxy(ExperienceTrackerHandler.getInstance().experienceTracker, proxyHandler);
	}, [params?.touchpointSource, getNameOverride]);

	return experienceTrackerProxy;
};
